import { isStudentView } from "../utils/studentView";
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";

const API = API_BASE_URL;

function formatMaybeDate(value) {
  if (!value) return "—";

  // sqlite timestamps are often "YYYY-MM-DD HH:MM:SS"
  const s = String(value);
  const isoGuess = s.includes("T") ? s : s.replace(" ", "T") + "Z";
  const d = new Date(isoGuess);
  if (Number.isNaN(d.getTime())) return s;

  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function StudentAccount({ outletContext } = {}) {
  const params = useParams();
  const routeOutlet = useOutletContext() || {};
  const {
    studentId: ctxStudentId,
    studentName,
    closeSidebarPanel,
  } = outletContext || routeOutlet;

  const studentId = useMemo(
    () => (ctxStudentId || params.id || params.studentId || "").toUpperCase(),
    [ctxStudentId, params.id, params.studentId]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);
  const [iconColor, setIconColor] = useState("default");
  const [savingColor, setSavingColor] = useState(false);
  const tutorMode = Boolean(outletContext?.tutorMode || routeOutlet?.tutorMode);

  const ICON_COLOR_OPTIONS = useMemo(
    () => [
      {
        key: "default",
        label: "Default",
        swatchStyle: {
          background: "conic-gradient(from 180deg,#cbbfa7,#dcd2bf,#cbbfa7)",
        },
      },
      { key: "muted", label: "Gold", swatchStyle: { background: "var(--sp-avatar-muted)" } },
      { key: "sand", label: "Sand", swatchStyle: { background: "var(--sp-avatar-sand)" } },
      { key: "ivory", label: "Ivory", swatchStyle: { background: "var(--sp-avatar-ivory)" } },
      {
        key: "red",
        label: "Red",
        swatchStyle: {
          background: "conic-gradient(from 180deg,#cbbfa7,#dcd2bf,#cbbfa7)",
          filter: "hue-rotate(330deg) saturate(1.35)",
        },
      },
      {
        key: "yellow",
        label: "Yellow",
        swatchStyle: {
          background: "conic-gradient(from 180deg,#cbbfa7,#dcd2bf,#cbbfa7)",
          filter: "hue-rotate(30deg) saturate(1.25)",
        },
      },
      {
        key: "turquoise",
        label: "Turquoise",
        swatchStyle: {
          background: "conic-gradient(from 180deg,#cbbfa7,#dcd2bf,#cbbfa7)",
          filter: "hue-rotate(160deg) saturate(1.25)",
        },
      },
      {
        key: "blue",
        label: "Blue",
        swatchStyle: {
          background: "conic-gradient(from 180deg,#cbbfa7,#dcd2bf,#cbbfa7)",
          filter: "hue-rotate(210deg) saturate(1.2)",
        },
      },
    ],
    []
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!studentId) {
        setLoading(false);
        setError("No student ID found.");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${API}/api/student_account/${studentId}`);
        const json = await res.json();

        if (!res.ok) {
          throw new Error(json?.detail || "Unable to load account details.");
        }

        if (!cancelled) {
          setAccount(json?.student || null);
          const c = json?.student?.icon_color || "default";
          setIconColor(c);
          try {
            localStorage.setItem(`student_icon_color_${studentId}`, c);
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Network or server error.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  async function saveIconColor(nextColor) {
    if (isStudentView()) return;
    if (!studentId) return;
    setSavingColor(true);
    setError(null);

    try {
      const res = await fetch(`${API}/api/student_account/${studentId}/icon_color`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ icon_color: nextColor }),
      });
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.detail || "Unable to save icon color.");
      }

      setIconColor(nextColor);
      setAccount((prev) => ({ ...(prev || {}), icon_color: nextColor }));
      try {
        localStorage.setItem(`student_icon_color_${studentId}`, nextColor);
      } catch (e) {
        // ignore
      }

      window.dispatchEvent(
        new CustomEvent("studentIconColorUpdated", {
          detail: { studentId, iconColor: nextColor },
        })
      );
    } catch (e) {
      setError(e?.message || "Network or server error.");
    } finally {
      setSavingColor(false);
    }
  }

  return (
    <section className="sp-content">
      <div className="sp-card sp-hero reveal" style={{ "--delay": ".05s" }}>
        <div className="sp-panel-header">
          <div>
            <div className="sp-hero-date">Account</div>
            <div className="sp-hero-title">{studentName || studentId}</div>
            <div className="sp-hero-sub">
              {tutorMode ? "Tutor workspace details." : "Your registration and access status."}
            </div>
          </div>
          {closeSidebarPanel && !tutorMode ? (
            <button
              type="button"
              className="sl-close-btn sp-panel-close-btn"
              onClick={() => closeSidebarPanel()}
              aria-label="Close account panel"
              title="Close account panel"
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      <div className="sp-columns">
        <section className="sp-cards" aria-label="Account details">
          {tutorMode ? (
            <>
              <div className="sp-card sp-action reveal" style={{ "--delay": ".12s" }}>
                <div>
                  <h2 className="sp-h2">Name</h2>
                  <p className="sp-text">{studentName || studentId || "—"}</p>
                </div>
              </div>

              <div className="sp-card sp-action reveal" style={{ "--delay": ".2s" }}>
                <div>
                  <h2 className="sp-h2">Role</h2>
                  <p className="sp-text">Tutor</p>
                </div>
              </div>

              <div className="sp-card sp-action reveal" style={{ "--delay": ".28s" }}>
                <div>
                  <h2 className="sp-h2">Tutor ID</h2>
                  <p className="sp-text">{studentId || "—"}</p>
                </div>
              </div>

              <div className="sp-card sp-action reveal" style={{ "--delay": ".34s" }}>
                <div>
                  <h2 className="sp-h2">Workspace</h2>
                  <p className="sp-text">Creative English Curriculum</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="sp-card sp-action reveal" style={{ "--delay": ".12s" }}>
                <div>
                  <h2 className="sp-h2">Registered</h2>
                  <p className="sp-text">
                    {loading ? "Loading…" : error ? "—" : formatMaybeDate(account?.registered_at)}
                  </p>
                </div>
              </div>

              <div className="sp-card sp-action reveal" style={{ "--delay": ".2s" }}>
                <div>
                  <h2 className="sp-h2">Last seen</h2>
                  <p className="sp-text">
                    {loading
                      ? "Loading…"
                      : error
                        ? "—"
                        : formatMaybeDate(account?.last_seen_at)}
                  </p>
                </div>
              </div>

              <div className="sp-card sp-action reveal" style={{ "--delay": ".28s" }}>
                <div>
                  <h2 className="sp-h2">Payment</h2>
                  <p className="sp-text">
                    {loading
                      ? "Loading…"
                      : error
                        ? "—"
                        : account?.payment_made
                          ? "Paid"
                          : "Not paid"}
                  </p>
                </div>
              </div>
            </>
          )}

          {!tutorMode ? (
            <div className="sp-card sp-action reveal" style={{ "--delay": ".34s" }}>
              <div>
                <h2 className="sp-h2">Icon colour</h2>
                <p className="sp-text">Choose a colour for your account icon.</p>

                <div className="sp-swatch-row" style={{ marginTop: 12 }}>
                  {ICON_COLOR_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      className={`sp-swatch ${iconColor === opt.key ? "selected" : ""}`}
                      style={opt.swatchStyle}
                      aria-label={`Set icon colour: ${opt.label}`}
                      disabled={savingColor || loading}
                      onClick={() => saveIconColor(opt.key)}
                    />
                  ))}
                </div>
                <div className="sp-swatch-label">
                  {savingColor ? "Saving…" : `Current: ${ICON_COLOR_OPTIONS.find((o) => o.key === iconColor)?.label || "Default"}`}
                </div>
              </div>
            </div>
          ) : null}

          {error && (
            <div className="sp-card sp-note reveal" style={{ "--delay": ".42s" }}>
              <h2 className="sp-h2">Couldn’t load account</h2>
              <p className="sp-text">{error}</p>
              <p className="sp-text">Make sure the backend is running on port 8000.</p>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
